import { MAX_TEXT_COMPONENT } from '../Article.context';
import { PT_TO_EM_FACTOR, PT_TO_EM_FACTOR_LINE_HEIGHT } from '../CSS';
import { Style } from '../../../constants';
import { Logger } from '../../../utils/Logger';

export const textComponentsSet = new Set([
	'headline',
	'title',
	'subtitle',
	'intro',
	'body',
	'crosshead',
	'byline',
	'blockquote',
	'footer',
	'imagecaption',
	'credit',
]);

for (let i = 0; i <= MAX_TEXT_COMPONENT; i++) {
	textComponentsSet.add(`text${i + 1}`);
}

export class VirtualCSS {
	selector: string;
	properties: any;
	style: Style;
	logger: Logger;
	spacer: string;
	isCustom: boolean = false;

	constructor(style: Style, selector?: string) {
		this.isCustom = style.type === 'custom';

		this.properties = style.properties;
		this.selector = selector ?? `.style-${style.id}`;

		this.style = style;
		this.logger = new Logger('CSS', {
			log: 'rgb(88, 86, 214)',
			debug: 'rgb(0, 113, 164)',
			info: 'rgb(36, 138, 61)',
		});

		this.spacer = this.isCustom ? '' : ' ';
	}

	getCSS(): string {
		const css = Object.keys(this.properties)
			.map(this.mapProperty)
			.filter((i) => i);
		return css.join(' ');
	}

	mapProperty = (property: string): string | null => {
		const properties = this.properties[property];
		if (textComponentsSet.has(property)) {
			return this.mapText(properties, property);
		}

		switch (property) {
			case 'canvas':
				return this.mapCanvas(properties);
			case 'spacer':
				return this.mapSpacer(properties);
			case 'divider':
				return this.mapDivider(properties);
			case 'dividers':
				return this.mapDividers(properties);
			case 'container':
				return this.mapContainer(properties);
			case 'anchor':
				return this.mapAnchor(properties);
			case 'image':
				return this.mapImage(properties);
			case 'bullet':
				return this.mapBullet(properties);
			case 'button':
				return this.mapButton(properties);
			case 'buttons':
				return this.mapButtons(properties);
			case 'gallery':
				return this.mapGalleries(properties);
			case 'styleblocks':
				return null;
			default:
				this.logger.warn(`Invalid property (${property})`, properties);
				return null;
		}

		return null;
	};

	mapText = (properties: any, component: string): string => {
		const response = [];
		const {
			backgroundColor,
			fontFamilyBold,
			fontFamilyBoldItalic,
			fontFamilyItalic,
			paragraphSpacing,
			anchor_color,
			anchor_text_decoration,
			anchor_text_decoration_color,
			textAlign,
			width,
			textDisplay,
			unit,
		} = properties;
		const margin = getMargin(properties);
		const padding = getPadding(properties);
		const border = getBorder(properties);
		const font = getFont(properties);
		const fontSize = getFontSize(properties);
		const dropcapFont = getDropcapFont(properties);

		const container = [margin, padding, border];
		const paragraphs = [font];

		if (paragraphSpacing !== undefined && unit?.paragraphSpacing) {
			response.push(`${this.selector} .${component} p:not(:last-child),
                ${this.selector} .${component} ul:not(:last-child),
                ${this.selector} .${component} ol:not(:last-child),
                ${this.selector} .${component} > div:not(:last-child) {
                /* Paragraph Spacing */
                margin-bottom: ${paragraphSpacing}${unit.paragraphSpacing};
            }`);
		}

		const containerCSS = container.filter((i) => i).join('\n');
		const paragraphCSS = paragraphs.filter((i) => i).join('\n');

		if (dropcapFont) {
			response.push(`${this.selector} .${component}.dropcap > :first-child::first-letter  {
                float: left;
                ${dropcapFont}
            }`);
		}

		if (backgroundColor) {
			response.push(`${this.selector} .${component} {
                background: #${backgroundColor.replace('#', '')};
            }`);
		}

		if (anchor_text_decoration && anchor_text_decoration !== 'inherit') {
			response.push(`${this.selector} .${component} a {
                text-decoration: ${anchor_text_decoration};
            }`);
		}

		if (anchor_text_decoration_color) {
			response.push(`${this.selector} .${component} a {
                text-decoration-color: #${anchor_text_decoration_color.replace(
				'#',
				'',
			)};
            }`);
		}

		if (textAlign) {
			response.push(`${this.selector} .${component} {
                text-align: ${textAlign};
            }`);
		}

		if (anchor_color) {
			response.push(`${this.selector} .${component} a {
                color: #${anchor_color.replace('#', '')};
            }`);
		}

		if (containerCSS.length) {
			response.push(`${this.selector} .${component} {
                ${containerCSS}
            }`);
		}

		if (paragraphCSS.length) {
			response.push(`${this.selector} .${component} p,
                ${this.selector} .${component} ul,
                ${this.selector} .${component} ol,
                ${this.selector} cite.${component},
                ${this.selector} .${component} > div {
                ${paragraphCSS}
            }`);
		}

		if (fontSize.length) {
			response.push(`${this.selector} .${component} > p,
                ${this.selector} .${component} > div,
                ${this.selector} .${component} > ul,
                ${this.selector} cite.${component},
                ${this.selector} .${component} > ol {
                ${fontSize}
            }`);
		}

		if (fontFamilyBold) {
			response.push(`${this.selector} .${component} b,
            ${this.selector} .${component} strong {
                font-family: '${fontFamilyBold}';
                font-weight: normal;
            }`);
		}

		if (fontFamilyItalic) {
			response.push(`${this.selector} .${component} em,
            ${this.selector} .${component} i {
                font-family: '${fontFamilyItalic}';
                font-weight: normal;
            }`);
		}

		if (fontFamilyBoldItalic) {
			response.push(`${this.selector} .${component} b i,
            ${this.selector} .${component} b em,
            ${this.selector} .${component} strong i,
            ${this.selector} .${component} strong em,
            ${this.selector} .${component} i strong,
            ${this.selector} .${component} em strong,
            ${this.selector} .${component} i b,
            ${this.selector} .${component} em b {
                font-family: '${fontFamilyBoldItalic}';
                font-weight: normal;
                font-style: normal;
            }`);
		}

		if (width) {
			response.push(`${this.selector} .${component} {
            width: ${width}%;
                }`);
		}

		if (textDisplay) {
			response.push(`${this.selector} .${component} {
        display: ${textDisplay};
      }`);
		}

		return response.join('\n');
	};

	mapCanvas = (properties: any): string => {
		const response = [];
		const { backgroundColor, bodyColor } = properties;

		const margin = getMargin(properties);
		const padding = getPadding(properties);
		const border = getBorder(properties);

		const css = [margin, padding, border];
		if (backgroundColor) {
			css.push(`background-color: #${backgroundColor.replace('#', '')};`);
		}

		const filteredCSS = css.filter((i) => i).join('\n');
		if (filteredCSS.length) {
			response.push(`${this.selector} .canvas > :first-child {
                ${filteredCSS}
            }`);
		}

		if (bodyColor) {
			response.push(`${this.selector} {
                background-color: #${bodyColor.replace('#', '')};
            }`);
		}

		response.push(this.mapBleed(properties));

		return response.join('\n');
	};

	mapBleed = (properties: any): string => {
		const defaultUnit = 'px';
		const response: Array<string> = [];
		const { paddingLeft, paddingRight, unit } = properties;
		if (!unit) {
			return '';
		}
		if (response.length) {
			response.unshift(`/* Bleed */`);
		}

		if (paddingLeft !== undefined) {
			response.push(`${this.selector} .canvas > div > .bleed-left {
                margin-left: -${paddingLeft}${unit['paddingLeft'] ?? defaultUnit
				} !important;
            }`);
			response.push(`${this.selector} .canvas > div > .bleed {
                margin-left: -${paddingLeft}${unit['paddingLeft'] ?? defaultUnit
				} !important;
            }`);
		}
		if (paddingRight !== undefined) {
			response.push(`${this.selector} .canvas > div > .bleed-right {
                margin-right: -${paddingRight}${unit['paddingRight'] ?? defaultUnit
				} !important;
            }`);
			response.push(`${this.selector} .canvas > div > .bleed {
                margin-right: -${paddingRight}${unit['paddingRight'] ?? defaultUnit
				} !important;
            }`);
		}

		return response.join('\n');
	};

	mapSpacer = (properties: any): string => {
		const {
			vSmallHeight,
			smallHeight,
			mediumHeight,
			largeHeight,
			vLargeHeight,
			unit,
		} = properties;
		if (!unit) {
			return '';
		}

		const response = [];
		if (vSmallHeight !== undefined && unit.vSmallHeight) {
			response.push(`${this.selector} .spacer.v-small {
                height: ${vSmallHeight}${unit.vSmallHeight};
            }`);
		}
		if (smallHeight !== undefined && unit.smallHeight) {
			response.push(`${this.selector} .spacer.small {
                height: ${smallHeight}${unit.smallHeight};
            }`);
		}
		if (mediumHeight !== undefined && unit.mediumHeight) {
			response.push(`${this.selector} .spacer.medium {
                height: ${mediumHeight}${unit.mediumHeight};
            }`);
		}
		if (largeHeight !== undefined && unit.largeHeight) {
			response.push(`${this.selector} .spacer.large {
                height: ${largeHeight}${unit.largeHeight};
            }`);
		}
		if (vLargeHeight !== undefined && unit.vLargeHeight) {
			response.push(`${this.selector} .spacer.v-large {
                height: ${vLargeHeight}${unit.vLargeHeight};
            }`);
		}
		return response.join('\n');
	};

	mapDividers = (properties: Array<any>) => {
		if (!properties.length) {
			return null;
		}

		const dividers = properties.map((d) => this.mapDivider(d));

		return dividers.join('\n');
	};

	mapDivider = (properties: any): string => {
		const id = properties.id ? `.${properties.id}` : '';
		const { style, align, unit, css } = properties;
		let { color, thickness, width } = properties;
		const margin = getMargin(properties);
		const padding = getPadding(properties);
		const response = [];
		let spacer: string = this.spacer;

		if (color === undefined) {
			color = '#000';
		} else {
			color = `#${color.replace('#', '')}`;
		}
		if (thickness === undefined) {
			thickness = '1px';
		} else {
			thickness = `${thickness}${unit?.['thickness'] || 'px'}`;
		}

		if (width === undefined) {
			width = 100;
		}
		if (css) {
			response.push(`${this.selector}${spacer}.divider${id} {${css}}`);
		}
		if (margin) {
			response.push(`${this.selector}${spacer}.divider${id} {${margin}}`);
		}
		if (padding) {
			response.push(`${this.selector}${spacer}.divider${id} {
                ${padding}
            }`);
		}
		if (align) {
			if (align === 'left') {
				response.push(`${this.selector}${spacer}.divider${id} hr {
                    margin-left: 0px;
                }`);
			} else if (align === 'right') {
				response.push(`${this.selector}${spacer}.divider${id} hr {
                    margin-right: 0px;
                }`);
			}
		}
		if (width) {
			response.push(`${this.selector}${spacer}.divider${id} hr {
                width: ${width}${unit['width'] || '%'};
		        box-sizing: content-box;
            }`);
		}
		switch (style) {
			case 'blur':
				response.push(`${this.selector}${spacer}.divider${id} hr {
					height: 0;
					box-shadow: 0 0 10px 1px ${color};
				}
				${this.selector} .divider hr:after{
					content: "\\00a0";
				}`);
				break;
			case 'dashed':
				response.push(`${this.selector}${spacer}.divider${id} hr {
					border-bottom: ${thickness} dashed ${color};
				}`);
				break;
			case 'dotted':
				response.push(`${this.selector}${spacer}.divider${id} hr {
				    border-bottom: ${thickness} dotted ${color};
			    }`);
				break;
			case 'chapter':
				response.push(`${this.selector}${spacer}.divider${id} hr {
						padding: 0;
						border-top: medium double ${color};
						color: ${color};
						text-align: center;
					}
					${this.selector}${spacer}.divider${id} hr:after {
						content: "§";
						display: inline-block;
						position: relative;
						top: -0.7em;
						font-size: 1.5em;
						padding: 0 0.25em;
					}`);
				break;
			case 'elegant':
				response.push(`${this.selector}${spacer}.divider${id} hr {
					height: 20px;
					border-style: solid;
					border-color: ${color};
					border-width: 1px 0 0 0;
					border-radius: 20px;
				}
				${this.selector}${spacer}.divider${id} hr:before {
					display: block;
					content: "";
					height: 20px;
					margin-top: -21px;
					border-style: solid;
					border-color: ${color};
					border-width: 0 0 1px 0;
					border-radius: 20px;
				}`);
				break;
			case 'innerfade':
				response.push(`${this.selector}${spacer}.divider${id} hr {
					height: 2px;
					background: ${color};
					background-image: -webkit-linear-gradient(left, #fff, ${color}, #fff);
					background-image: linear-gradient(left, #fff, ${color}, #fff);
				}`);
				break;
			case 'inset':
				response.push(`${this.selector}${spacer}.divider${id} hr {
					height: 0;
					border-top: 1px solid rgba(0, 0, 0, 0.1);
					border-bottom: 1px solid rgba(255, 255, 255, 0.3);
				}`);
				break;
			case 'shadow':
				response.push(`${this.selector}${spacer}.divider${id} hr {
					height: 12px;
					  box-shadow: inset 0 10px 12px -12px rgba(0,0,0,0.5);
				}`);
				break;
			default:
				response.push(`${this.selector}${spacer}.divider${id} hr {
					border-bottom: ${thickness} solid ${color};
				}`);
		}
		return response.join('\n');
	};

	mapContainer = (properties: any): string => {
		const response = [];
		const { unit, backgroundColor, borderRadius } = properties;

		const margin = getMargin(properties);
		const padding = getPadding(properties);
		const border = getBorder(properties);

		const css = [margin, padding, border];
		if (backgroundColor) {
			css.push(`background-color: #${backgroundColor.replace('#', '')};`);
		}

		if (borderRadius && unit?.borderRadius) {
			css.push(`border-radius: ${borderRadius}${unit?.borderRadius};`);
		}

		const filteredCSS = css.filter((i) => i).join('\n');
		if (filteredCSS.length) {
			response.push(`${this.selector} {
                ${filteredCSS}
            }`);
		}

		return response.join('\n');
	};

	mapAnchor = (properties: any): string => {
		const response = [];
		const margin = getMargin(properties);
		const padding = getPadding(properties);
		const border = getBorder(properties);
		const font = getFont(properties);
		const fontSize = getFontSize(properties);

		const css = [margin, padding, border, font, fontSize];

		const filteredCSS = css.filter((i) => i).join('\n');

		if (filteredCSS.length) {
			response.push(`${this.selector} a {
                ${filteredCSS}
            }`);
		}

		return response.join('\n');
	};

	mapImage = (properties: any): string => {
		const response = [];
		const margin = getMargin(properties);
		const padding = getPadding(properties);

		const css = [margin, padding];
		const filteredCSS = css.filter((i) => i).join('\n');
		if (filteredCSS.length) {
			response.push(`${this.selector} .image {
                ${filteredCSS}
            }`);
		}

		return response.join('\n');
	};

	mapBullet = (properties: any): string => {
		const { listStyle } = properties;
		const response = [];
		const margin = getMargin(properties);
		const padding = getPadding(properties);
		const border = getBorder(properties);
		const font = getFont(properties);
		const fontSize = getFontSize(properties);

		const css = [margin, padding, border, font, fontSize];
		if (listStyle) {
			css.push(`list-style: ${listStyle};`);
		}

		const filteredCSS = css.filter((i) => i).join('\n');

		if (filteredCSS.length) {
			response.push(`${this.selector} a {
                ${filteredCSS}
            }`);
		}

		return response.join('\n');
	};

	mapButtons = (buttonsProperties: Array<any>): string => {
		if (!buttonsProperties.length) {
			return '';
		}
		const response: Array<string> = [];
		for (const properties of buttonsProperties) {
			const css = this.mapButton(properties)
			response.push(css);
		}

		return response.join('\n');
	};

	mapButton = (properties: any): string => {
		const { isCustom } = this;
		const { unit } = properties;
		let {
			align,
			backgroundColor,
			color,
			fontFamily,
			fontSize,
			letterSpacing,
			marginBottom,
			marginTop,
		} = properties;

		align = align ? `text-align: ${align};` : '';
		backgroundColor = backgroundColor
			? `background: #${backgroundColor.replace('#', '')};`
			: '';
		fontFamily = fontFamily ? `font-family: '${fontFamily}';` : '';
		if (fontSize && unit && unit['fontSize'] === 'pt') {
			fontSize = fontSize
				? `font-size: ${fontSize * PT_TO_EM_FACTOR}em;`
				: '';
		} else {
			fontSize = fontSize
				? `font-size: ${fontSize}${unit['fontSize']};`
				: '';
		}
		color = color ? `color: #${color.replace('#', '')};` : '';

		letterSpacing =
			letterSpacing !== undefined
				? `letter-spacing: ${parseFloat(letterSpacing)}${unit['letterSpacing']
				};`
				: '';
		marginTop =
			marginTop !== undefined
				? `margin-top: ${marginTop}${unit['marginTop']};`
				: '';
		marginBottom =
			marginBottom !== undefined
				? `margin-bottom: ${marginBottom}${unit['marginBottom']};`
				: '';
		const textTransform = properties['case']
			? `text-transform: ${properties['case']};`
			: '';

		const buttonSelector = properties.id ? `button.${properties.id}` : 'button'

		const styleSelector = isCustom ? `button.btn-${this.style.id}` : `${this.selector} ${buttonSelector}`;

		const css = `${styleSelector} {
                cursor: pointer;
                border: none;
                box-shadow: 0 1px 1px rgba(0, 0, 0, 0.16), 0 1px 1px rgba(0, 0, 0, 0.12);
                border-radius: 4px;
                ${align}
                ${backgroundColor}
                ${fontFamily}
                ${fontSize}
                ${color}
                ${letterSpacing}
                ${marginBottom}
                ${marginTop}
                ${textTransform}
            }`;

		return css;
	}

	mapGalleries = (properties: any): string => {
		const response = [];
		const margin = getMargin(properties);
		const padding = getPadding(properties);

		const css = [margin, padding];

		const filteredCSS = css.filter((i) => i).join('\n');

		if (filteredCSS.length) {
			response.push(`${this.selector} .gallery {
                ${filteredCSS}
            }`);
		}

		return response.join('\n');
	};
}

function getMargin(properties: any): string {
	const defaultUnit = 'px';
	const response: Array<string> = [];
	const { marginBottom, marginTop, marginLeft, marginRight, unit } =
		properties;

	// If there isn't a unit i will not process it
	if (!unit) {
		return '';
	}

	if (marginTop !== undefined) {
		response.push(
			`margin-top: ${marginTop}${unit['marginTop'] ?? defaultUnit};`,
		);
	}
	if (marginBottom !== undefined) {
		response.push(
			`margin-bottom: ${marginBottom}${unit['marginBottom'] ?? defaultUnit
			};`,
		);
	}
	if (marginLeft !== undefined) {
		response.push(
			`margin-left: ${marginLeft}${unit['marginLeft'] ?? defaultUnit};`,
		);
	}
	if (marginRight !== undefined) {
		response.push(
			`margin-right: ${marginRight}${unit['marginRight'] ?? defaultUnit
			};`,
		);
	}

	if (response.length) {
		response.unshift(`/* Margin */`);
	}

	return response.join('\n');
}

function getPadding(properties: any): string {
	const defaultUnit = 'px';
	const response: Array<string> = [];
	const { paddingBottom, paddingTop, paddingLeft, paddingRight, unit } =
		properties;

	// If there isn't a unit i will not process it
	if (!unit) {
		return '';
	}

	if (paddingTop !== undefined) {
		response.push(
			`padding-top: ${paddingTop}${unit['paddingTop'] ?? defaultUnit};`,
		);
	}
	if (paddingBottom !== undefined) {
		response.push(
			`padding-bottom: ${paddingBottom}${unit['paddingBottom'] ?? defaultUnit
			};`,
		);
	}
	if (paddingLeft !== undefined) {
		response.push(
			`padding-left: ${paddingLeft}${unit['paddingLeft'] ?? defaultUnit
			};`,
		);
	}
	if (paddingRight !== undefined) {
		response.push(
			`padding-right: ${paddingRight}${unit['paddingRight'] ?? defaultUnit
			};`,
		);
	}

	if (response.length) {
		response.unshift(`/* Padding */`);
	}

	return response.join('\n');
}

function getBorder(properties: any): string {
	const defaultUnit = 'px';
	const response: Array<string> = [];
	const {
		borderWidth,
		borderColor,
		borderEdge,
		borderType,
		borderRadius,
		borderRadiusPosition,
		unit,
	} = properties;

	if (borderRadius) {
		if (borderRadiusPosition === 'all') {
			const borderRadiusCSS = `border-radius: ${borderRadius}${unit?.borderRadius ?? defaultUnit
				};`;
			response.push(borderRadiusCSS);
		} else {
			if (Array.isArray(borderRadiusPosition)) {
				response.push(
					borderRadiusPosition
						.map((edge: string) => {
							return `border-${edge}-radius: ${borderRadius}${unit?.borderRadius ?? defaultUnit
								};`;
						})
						.join('\n'),
				);
			}
		}
	}

	if (borderType === 'none') {
		response.push(`
        /* Border None*/
        border: 0px;
        `);
		response.unshift(`/* Border */`);
		return response.join('\n');
	}

	if (borderType) {
		response.push(`border-style: ${borderType};`);
	}

	if (borderEdge) {
		if (borderEdge === 'all') {
			response.push(
				`border: ${borderWidth || 1}${unit?.borderWidth ?? defaultUnit
				} ${borderType || 'solid'};`,
			);
		} else {
			response.push(
				borderEdge
					.split('-')
					.map((edge: string) => {
						return `border-${edge}: ${borderWidth}${unit?.borderWidth ?? defaultUnit
							} ${borderType || 'solid'};`;
					})
					.join('\n'),
			);
		}
	}

	if (borderColor) {
		response.push(`border-color: #${borderColor.replace('#')};`);
	}

	/*if (borderWidth !== undefined) {
		response.push(`border-width: ${borderWidth}${unit?.borderWidth ?? defaultUnit};`)
	}*/

	if (response.length) {
		response.unshift(`/* Border */`);
	}

	return response.join('\n');
}

function getFont(properties: any): string {
	const {
		color,
		fontFamily,
		fontStyle,
		letterSpacing,
		textAlign,
		textDecoration,
		textIndent,
		textTransform,
		unit,
	} = properties;

	const response: Array<string> = [];

	if (color) {
		response.push(`color: #${color.replace('#', '')};`);
	}

	if (fontFamily) {
		response.push(`font-family: '${fontFamily}';`);
	}

	if (fontStyle) {
		response.push(`font-style: ${fontStyle};`);
	}

	if (letterSpacing !== undefined && unit?.letterSpacing) {
		if (unit.letterSpacing === 'pt') {
			response.push(
				`letter-spacing: ${parseFloat(`${letterSpacing}`) * PT_TO_EM_FACTOR
				}rem;`,
			);
		} else {
			response.push(
				`letter-spacing: ${letterSpacing}${unit.letterSpacing};`,
			);
		}
	}

	if (textAlign) {
		response.push(`text-align: ${textAlign};`);
	}

	if (textDecoration) {
		response.push(`text-decoration: ${textDecoration};`);
	}

	if (textIndent !== undefined && unit?.textIndent) {
		response.push(`text-indent: ${textIndent}${unit.textIndent};`);
	}

	if (textTransform) {
		response.push(`text-transform: ${textTransform};`);
	}

	if (response.length) {
		response.unshift(`/* Fonts */`);
	}

	return response.join('\n');
}

function getFontSize(properties: any): string {
	const { fontSize, lineHeight, unit } = properties;

	const response: Array<string> = [];

	if (fontSize !== undefined && unit?.fontSize) {
		if (unit.fontSize === 'pt') {
			response.push(
				`font-size: ${parseFloat(`${fontSize}`) * PT_TO_EM_FACTOR}em;`,
			);
		} else {
			response.push(`font-size: ${fontSize}${unit.fontSize};`);
		}
	}

	if (lineHeight !== undefined && unit?.lineHeight) {
		if (unit.lineHeight === 'pt') {
			const lineHeightValue =
				(parseFloat(`${lineHeight}`) * PT_TO_EM_FACTOR_LINE_HEIGHT) /
				1.6;

			response.push(`line-height: ${lineHeightValue}rem`);
		} else {
			response.push(`line-height: ${lineHeight}${unit.lineHeight};`);
		}
	}

	if (response.length) {
		response.unshift(`/* Fonts Size */`);
	}

	return response.join('\n');
}

function getDropcapFont(properties: any): string {
	const {
		dropcapFontSize,
		dropcapLineHeight,
		dropcapColor,
		dropcapBackgroundColor,
		dropcapFontFamily,
		dropcapPaddingRight,
		dropcapPaddingLeft,
		dropcapPaddingTop,
		dropcapPaddingBottom,
		dropcapMarginTop,
		dropcapMarginBottom,
		dropcapMarginLeft,
		dropcapMarginRight,
		unit,
	} = properties;
	const response: Array<string> = [];

	if (dropcapFontSize !== undefined && unit?.dropcapFontSize === 'pt') {
		response.push(`font-size: ${parseFloat(dropcapFontSize)}pt;`);
	} else {
		response.push(
			dropcapFontSize
				? `font-size: ${parseFloat(`${dropcapFontSize}`)}${unit['dropcapFontSize']
				};`
				: `font-size: 4em;`,
		);
	}

	if (dropcapLineHeight !== undefined) {
		response.push(
			`line-height: ${dropcapLineHeight}${unit['dropcapLineHeight']};`,
		);
	}

	if (dropcapFontFamily !== undefined) {
		response.push(`font-family: '${dropcapFontFamily}';`);
	}

	if (dropcapColor !== undefined) {
		response.push(`color: #${dropcapColor.replace('#', '')};`);
	}

	if (dropcapBackgroundColor !== undefined) {
		response.push(
			`background: #${dropcapBackgroundColor.replace('#', '')};`,
		);
	}

	/* Margin */

	if (dropcapMarginTop !== undefined && unit?.['dropcapMarginTop']) {
		response.push(
			`margin-top: ${dropcapMarginTop}${unit['dropcapMarginTop']};`,
		);
	}

	if (dropcapMarginBottom !== undefined && unit?.['dropcapMarginBottom']) {
		response.push(
			`margin-bottom: ${dropcapMarginBottom}${unit['dropcapMarginBottom']};`,
		);
	}

	if (dropcapMarginLeft !== undefined && unit?.['dropcapMarginLeft']) {
		response.push(
			`margin-left: ${dropcapMarginLeft}${unit['dropcapMarginLeft']};`,
		);
	}

	if (dropcapMarginRight !== undefined && unit?.['dropcapMarginRight']) {
		response.push(
			`margin-right: ${dropcapMarginRight}${unit['dropcapMarginRight']};`,
		);
	}

	/* Padding */

	if (dropcapPaddingTop !== undefined && unit?.['dropcapPaddingTop']) {
		response.push(
			`padding-top: ${dropcapPaddingTop}${unit['dropcapPaddingTop']};`,
		);
	}

	if (dropcapPaddingBottom !== undefined && unit?.['dropcapPaddingBottom']) {
		response.push(
			`padding-bottom: ${dropcapPaddingBottom}${unit['dropcapPaddingBottom']};`,
		);
	}

	if (dropcapPaddingLeft !== undefined && unit?.['dropcapPaddingLeft']) {
		response.push(
			`padding-left: ${dropcapPaddingLeft}${unit['dropcapPaddingLeft']};`,
		);
	}

	if (dropcapPaddingRight !== undefined && unit?.['dropcapPaddingRight']) {
		response.push(
			`padding-right: ${dropcapPaddingRight}${unit['dropcapPaddingRight']};`,
		);
	}

	if (response.length) {
		response.unshift(`/* Dropcap */`);
	}
	return response.join('\n');
}

export type DeviceType = 'mobile' | 'desktop' | 'tablet';
